import { useState } from "react";
import { Loader2, PlugZap } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { ImageField, Panel, TextField } from "./fields";
import { ExtrasPanel } from "./ExtrasPanel";
import { Checkbox } from "../../components/ui/checkbox";
import { GalleryField } from "./GalleryField";

const Connection = ({ provider }) => {
  const [busy, setBusy] = useState(false);
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/connection/test");
      toast.success(`Connected: ${data.hotels} hotel(s), ${data.rooms} room type(s)`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  const live = provider === "eres";
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-ink/10 bg-white p-4 lg:col-span-2" data-testid="admin-connection">
      <p className="text-sm">
        Data source: <b data-testid="admin-provider">{live ? "eRes live reservation system" : "Demo data (eRes not connected)"}</b>
      </p>
      <button type="button" className="btn-outline !py-2" onClick={test} disabled={busy} data-testid="admin-test-connection">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />}Test connection
      </button>
    </div>
  );
};

const EMPTY = { name: "", tagline: "", description: "", image: "", show_image: true, show_description: true, rooms: {}, services: {} };

export const HotelsTab = ({ catalog, overrides, setOverrides, provider, ownerContent = {}, ownerValues = {} }) => {
  const [sel, setSel] = useState(catalog[0]?.id);
  const hotel = catalog.find((h) => h.id === sel);
  if (!hotel) return <Connection provider={provider} />;
  const o = { ...EMPTY, ...overrides[sel] };
  const w = ownerValues[sel] || { rooms: {}, services: {} };
  const ownerImg = (u) => (u ? "Using the verified owner's photo" : undefined);
  const setHotel = (p) => setOverrides({ ...overrides, [sel]: { ...o, ...p } });
  const setRoom = (rid, p) => setHotel({ rooms: { ...o.rooms, [rid]: { name: "", description: "", image: "", gallery: [], ...o.rooms[rid], ...p } } });

  return (
    <div className="grid gap-6 lg:grid-cols-[260px_1fr]" data-testid="admin-hotels-tab">
      <Connection provider={provider} />
      <ul className="space-y-1 lg:sticky lg:top-24 lg:self-start">
        {catalog.map((h) => (
          <li key={h.id}>
            <button type="button" onClick={() => setSel(h.id)} data-testid={`admin-hotel-${h.id}`}
              className={`w-full rounded-xl px-4 py-3 text-left text-sm transition-colors ${h.id === sel ? "bg-brand text-sand" : "hover:bg-white"}`}>
              {overrides[h.id]?.name || ownerValues[h.id]?.name || h.name}
              <span className={`block text-xs ${h.id === sel ? "text-sand/70" : "text-ink/45"}`}>{h.city}</span>
            </button>
          </li>
        ))}
      </ul>
      <div className="space-y-6">
        <Panel title={o.name || w.name || hotel.name} hint={ownerContent[sel]
          ? `Greyed hints show what guests see now: the verified owner's (${ownerContent[sel]}) content, or eRes where the owner left it blank. Anything you type replaces it on your site.`
          : "Leave a field blank to use the text and photo from the reservation system."}>
          {ownerContent[sel] && <p className="mb-4 rounded-xl bg-brand/10 px-4 py-2 text-sm text-ink/80" data-testid="hotel-owner-content-note">Content shared by verified owner: {ownerContent[sel]}</p>}
          <div className="grid gap-5 md:grid-cols-2">
            <div className="md:col-span-2"><ImageField label="Main photo" value={o.image} placeholder={w.image || hotel.image} placeholderLabel={ownerImg(w.image)} onChange={(v) => setHotel({ image: v })} testid="hotel-image" /></div>
            <TextField label="Display name" value={o.name} placeholder={w.name || hotel.name} onChange={(v) => setHotel({ name: v })} testid="hotel-name-input" />
            <TextField label="Tagline" value={o.tagline} placeholder={w.tagline || hotel.tagline} onChange={(v) => setHotel({ tagline: v })} testid="hotel-tagline-input" />
            <div className="md:col-span-2"><TextField multiline label="Description" value={o.description} placeholder={w.description || hotel.description} onChange={(v) => setHotel({ description: v })} testid="hotel-description-input" /></div>
            <div className="space-y-2 rounded-xl bg-linen p-4 text-sm text-ink/75 md:col-span-2" data-testid="hotel-display-options">
              <p className="field-label">On the booking (rooms & rates) page</p>
              <label className="flex items-center gap-3"><Checkbox checked={o.show_image !== false} onCheckedChange={(v) => setHotel({ show_image: !!v })} data-testid="hotel-show-image" />Show the main photo</label>
              <label className="flex items-center gap-3"><Checkbox checked={o.show_description !== false} onCheckedChange={(v) => setHotel({ show_description: !!v })} data-testid="hotel-show-description" />Show the description</label>
              <p className="text-xs text-ink/50">Untick these if your home page or gallery already shows them, so guests go straight to the rooms. The hotel list keeps its photo.</p>
            </div>
          </div>
        </Panel>
        <Panel title="Rooms">
          <div className="space-y-5">
            {hotel.rooms.map((r) => {
              const ro = o.rooms[r.id] || {};
              const wr = w.rooms?.[r.id] || {};
              return (
                <div key={r.id} className="grid gap-4 rounded-xl border border-ink/10 p-4 md:grid-cols-2" data-testid={`admin-room-${r.id}`}>
                  <div className="md:col-span-2"><ImageField label={`${wr.name || r.name} photo`} value={ro.image || ""} placeholder={wr.image || r.image} placeholderLabel={ownerImg(wr.image)} onChange={(v) => setRoom(r.id, { image: v })} testid={`room-${r.id}-image`} /></div>
                  <TextField label="Display name" value={ro.name || ""} placeholder={wr.name || r.name} onChange={(v) => setRoom(r.id, { name: v })} testid={`room-${r.id}-name`} />
                  <TextField multiline label="Description" value={ro.description || ""} placeholder={wr.description || r.description} onChange={(v) => setRoom(r.id, { description: v })} testid={`room-${r.id}-description`} />
                  <div className="md:col-span-2"><GalleryField label="More photos" value={ro.gallery || []} inherited={wr.gallery || []} fromEres={Math.max(0, (r.images?.length || 0) - (r.image ? 1 : 0))} onChange={(g) => setRoom(r.id, { gallery: g })} testid={`room-${r.id}-gallery`} /></div>
                </div>
              );
            })}
          </div>
        </Panel>
        <ExtrasPanel hotelId={sel} values={o.services} owner={w.services || {}} onChange={(services) => setHotel({ services })} />
      </div>
    </div>
  );
};
